import { ActionBar, Input, Radio, Select, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { useLoopStore } from '../store'
import type { LoopScheduleTabInput } from './types'

export const LoopScheduleTab = () => {
	const { organizationId: _organizationId, loopId } = useParams<{
		organizationId: string
		loopId: string
	}>()
	const navigate = useNavigate()
	const toast = useToast()
	const { loop, useEditLoop } = useLoopStore()
	const { editLoop, isLoading: isEditing } = useEditLoop(loop?.id)

	const {
		handleSubmit,
		control,
		watch,
		formState: { errors },
		reset,
	} = useForm<LoopScheduleTabInput>({
		defaultValues: {
			scheduleType: loop?.scheduleType || 'one-time',
			scheduleFrequency: loop?.scheduleFrequency || null,
			scheduleStartDate: loop?.scheduleStartDate || null,
			scheduleEndDate: loop?.scheduleEndDate || null,
			iterationDurationDays: 7,
		},
	})

	const scheduleType = watch('scheduleType')
	const scheduleFrequency = watch('scheduleFrequency')
	const scheduleStartDate = watch('scheduleStartDate')

	useEffect(() => {
		if (loop) {
			reset({
				scheduleType: loop.scheduleType || 'one-time',
				scheduleFrequency: loop.scheduleFrequency || null,
				scheduleStartDate: loop.scheduleStartDate || null,
				scheduleEndDate: loop.scheduleEndDate || null,
				iterationDurationDays: 7,
			})
		}
	}, [loop, reset])

	const handleSubmitInternal = async (data: LoopScheduleTabInput) => {
		if (!loop?.id) {
			toast.showToast('Please create the loop first (General tab)', 'error')
			return
		}

		// Validate dates
		if (data.scheduleStartDate && data.scheduleEndDate) {
			const start = new Date(data.scheduleStartDate)
			const end = new Date(data.scheduleEndDate)
			if (end <= start) {
				toast.showToast('End date must be after start date', 'error')
				return
			}
		}

		await editLoop({
			scheduleType: data.scheduleType,
			scheduleFrequency: data.scheduleType === 'recurring' ? data.scheduleFrequency : null,
			scheduleStartDate: data.scheduleStartDate,
			scheduleEndDate: data.scheduleEndDate,
		})
	}

	const frequencyOptions = [
		{ value: 'daily', label: 'Daily' },
		{ value: 'weekly', label: 'Weekly' },
		{ value: 'monthly', label: 'Monthly' },
		{ value: 'quarterly', label: 'Quarterly' },
	]

	// Calculate preview of next iterations
	const nextIterations = useMemo(() => {
		if (!scheduleStartDate || scheduleType !== 'recurring' || !scheduleFrequency) {
			return []
		}

		const start = new Date(scheduleStartDate)
		const iterations: string[] = []
		const currentDate = new Date(start)

		for (let i = 0; i < 5; i++) {
			iterations.push(currentDate.toLocaleDateString())

			// Calculate next iteration based on frequency
			switch (scheduleFrequency) {
				case 'daily':
					currentDate.setDate(currentDate.getDate() + 1)
					break
				case 'weekly':
					currentDate.setDate(currentDate.getDate() + 7)
					break
				case 'monthly':
					currentDate.setMonth(currentDate.getMonth() + 1)
					break
				case 'quarterly':
					currentDate.setMonth(currentDate.getMonth() + 3)
					break
			}
		}

		return iterations
	}, [scheduleStartDate, scheduleType, scheduleFrequency])

	const isPending = isEditing

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 gap-8 px-6 max-w-4xl'>
				{/* Schedule Type Selection */}
				<div className='space-y-4'>
					<Text variant='small' weight='semibold'>
						Schedule Type
					</Text>
					<Controller
						name='scheduleType'
						control={control}
						render={({ field }) => (
							<div className='space-y-3'>
								<div className='flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50'>
									<Radio
										id='schedule-onetime'
										name='scheduleType'
										value='one-time'
										checked={field.value === 'one-time'}
										onChange={() => field.onChange('one-time')}
									/>
									<div>
										<Text variant='small' weight='semibold'>
											One-time Loop
										</Text>
										<Text variant='xsmall' className='text-gray-600 mt-1'>
											Execute this loop once during a specific time period
										</Text>
									</div>
								</div>

								<div className='flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50'>
									<Radio
										id='schedule-recurring'
										name='scheduleType'
										value='recurring'
										checked={field.value === 'recurring'}
										onChange={() => field.onChange('recurring')}
									/>
									<div>
										<Text variant='small' weight='semibold'>
											Recurring Loop
										</Text>
										<Text variant='xsmall' className='text-gray-600 mt-1'>
											Execute this loop on a regular schedule (daily, weekly, monthly, etc.)
										</Text>
									</div>
								</div>
							</div>
						)}
					/>
				</div>

				{/* Frequency Selection (Recurring only) */}
				{scheduleType === 'recurring' && (
					<div>
						<Controller
							name='scheduleFrequency'
							control={control}
							rules={{
								required: scheduleType === 'recurring' ? 'Frequency is required' : false,
							}}
							render={({ field }) => (
								<Select
									label='Frequency'
									options={frequencyOptions}
									value={field.value || ''}
									onChange={field.onChange}
									variant={errors.scheduleFrequency ? 'error' : 'default'}
									hint={errors.scheduleFrequency?.message || 'How often should this loop repeat?'}
									isRequired
								/>
							)}
						/>
					</div>
				)}

				{/* Date Range */}
				<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
					<div>
						<Controller
							name='scheduleStartDate'
							control={control}
							rules={{
								required: 'Start date is required',
							}}
							render={({ field }) => (
								<Input
									label='Start Date'
									type='date'
									value={field.value || ''}
									onChange={field.onChange}
									onBlur={field.onBlur}
									variant={errors.scheduleStartDate ? 'error' : 'default'}
									hint={
										errors.scheduleStartDate?.message ||
										(scheduleType === 'one-time'
											? 'When should this loop start?'
											: 'When should the first iteration launch?')
									}
									required
								/>
							)}
						/>
					</div>

					<div>
						<Controller
							name='scheduleEndDate'
							control={control}
							render={({ field }) => (
								<Input
									label={`End Date ${scheduleType === 'recurring' ? '(Optional)' : ''}`}
									type='date'
									value={field.value || ''}
									onChange={field.onChange}
									onBlur={field.onBlur}
									variant={errors.scheduleEndDate ? 'error' : 'default'}
									hint={
										errors.scheduleEndDate?.message ||
										(scheduleType === 'one-time' ? 'When should this loop close?' : 'Leave empty for ongoing loop')
									}
								/>
							)}
						/>
					</div>
				</div>

				{/* Next Iterations Preview (Recurring only) */}
				{scheduleType === 'recurring' && nextIterations.length > 0 && (
					<div className='bg-blue-50 p-4 rounded-lg space-y-3'>
						<Text variant='small' weight='semibold' className='text-blue-900'>
							Next 5 Iterations Preview
						</Text>
						<div className='space-y-2'>
							{nextIterations.map((date, index) => (
								<div key={date} className='flex items-center gap-3'>
									<div className='w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center'>
										<Text variant='xsmall' weight='semibold' className='text-blue-900'>
											{index + 1}
										</Text>
									</div>
									<Text variant='xsmall' className='text-blue-800'>
										{date}
									</Text>
								</div>
							))}
						</div>
						<Text variant='xsmall' className='text-blue-700 italic'>
							These are estimated dates based on your frequency selection
						</Text>
					</div>
				)}

				{/* Helpful Tips */}
				<div className='bg-gray-50 p-4 rounded-lg'>
					<Text variant='small' weight='semibold'>
						💡 Tips
					</Text>
					<ul className='mt-2 ml-4 space-y-2 list-disc list-inside'>
						<li>
							<Text variant='xsmall' className='text-gray-700'>
								{scheduleType === 'one-time'
									? 'One-time loops are perfect for surveys or feedback that only needs to happen once'
									: 'Recurring loops are great for regular check-ins, pulse surveys, and ongoing feedback'}
							</Text>
						</li>
						<li>
							<Text variant='xsmall' className='text-gray-700'>
								Participants will receive an email notification when each iteration launches
							</Text>
						</li>
						{scheduleType === 'recurring' && (
							<li>
								<Text variant='xsmall' className='text-gray-700'>
									You can pause or stop a recurring loop at any time from the loop overview page
								</Text>
							</li>
						)}
					</ul>
				</div>
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Back',
						onClick: () => navigate(`/loops/${loopId}/configuration`),
					},
					{
						variant: 'primary' as const,
						label: 'Save & Continue',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
		</form>
	)
}
